import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { geofencesApi } from '../services/api';
import { useTenantContextStore } from '../store/tenant-context.store';
import maplibregl, { Map as MapLibreMap, Marker } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  X,
  MapPin,
  Circle,
  Hexagon,
  Route,
  Eye,
  EyeOff,
  Loader2,
  AlertCircle,
  Check,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import clsx from 'clsx';

interface Geofence {
  id: string;
  name: string;
  type: 'CIRCLE' | 'POLYGON' | 'CORRIDOR';
  color: string;
  centerLatitude: number | null;
  centerLongitude: number | null;
  radius: number | null;
  coordinates: [number, number][] | null;
  corridorWidth: number | null;
  category: string | null;
  isActive: boolean;
  createdAt: string;
}

interface GeofenceFormData {
  name: string;
  type: 'CIRCLE' | 'POLYGON' | 'CORRIDOR';
  color: string;
  centerLatitude: number;
  centerLongitude: number;
  radius: number;
  coordinates: [number, number][];
  category: string;
  isActive: boolean;
}

const defaultFormData: GeofenceFormData = {
  name: '',
  type: 'CIRCLE',
  color: '#3B82F6',
  centerLatitude: 48.8566,
  centerLongitude: 2.3522,
  radius: 500,
  coordinates: [],
  category: '',
  isActive: true,
};

const colorOptions = [
  '#3B82F6', // blue
  '#10B981', // green
  '#F59E0B', // amber
  '#EF4444', // red
  '#8B5CF6', // violet
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#6B7280', // gray
];

const categoryOptions = [
  { value: '', label: 'Sans categorie' },
  { value: 'depot', label: 'Depot' },
  { value: 'client', label: 'Client' },
  { value: 'forbidden', label: 'Zone interdite' },
  { value: 'speed', label: 'Zone de vitesse' },
  { value: 'poi', label: 'Point d\'interet' },
];

const MAP_STYLE = 'https://tiles.openfreemap.org/styles/liberty';

export default function GeofencesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { selectedTenant } = useTenantContextStore();
  const tenantKey = selectedTenant?.id || 'default';
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<MapLibreMap | null>(null);
  const centerMarker = useRef<Marker | null>(null);

  // State
  const [sidebarOpen, setSidebarOpen] = useState(() => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedGeofence, setSelectedGeofence] = useState<Geofence | null>(null);
  const [formData, setFormData] = useState<GeofenceFormData>(defaultFormData);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof GeofenceFormData | 'coordinates', string>>>({});
  const [visibleGeofences, setVisibleGeofences] = useState<Set<string>>(new Set());
  const [isPlacingCenter, setIsPlacingCenter] = useState(false);
  const [isDrawingPolygon, setIsDrawingPolygon] = useState(false);
  const polygonMarkersRef = useRef<Marker[]>([]);

  // Fetch geofences
  const { data, isLoading } = useQuery({
    queryKey: ['geofences', tenantKey],
    queryFn: async () => {
      const response = await geofencesApi.list({ limit: 100 });
      return response.data;
    },
  });

  // Ensure geofences is always an array
  const geofences: Geofence[] = Array.isArray(data?.data) ? data.data : [];

  // Filter geofences
  const filteredGeofences = geofences.filter((g) =>
    g.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    g.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Stats
  const stats = {
    total: geofences.length,
    active: geofences.filter((g) => g.isActive).length,
    circles: geofences.filter((g) => g.type === 'CIRCLE').length,
    polygons: geofences.filter((g) => g.type === 'POLYGON').length,
  };

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: MAP_STYLE,
      center: [6.1296, 49.8153], // Luxembourg center
      zoom: 9,
    });

    map.current.addControl(new maplibregl.NavigationControl(), 'top-right');

    return () => {
      map.current?.remove();
      map.current = null;
    };
  }, []);

  // Update map click handler when isPlacingCenter or isDrawingPolygon changes
  useEffect(() => {
    if (!map.current) return;

    const handleClick = (e: maplibregl.MapMouseEvent) => {
      if (isPlacingCenter) {
        setFormData((prev) => ({
          ...prev,
          centerLatitude: e.lngLat.lat,
          centerLongitude: e.lngLat.lng,
        }));
        setIsPlacingCenter(false);
      } else if (isDrawingPolygon) {
        const newPoint: [number, number] = [e.lngLat.lng, e.lngLat.lat];
        setFormData((prev) => ({
          ...prev,
          coordinates: [...prev.coordinates, newPoint],
        }));
      }
    };

    map.current.on('click', handleClick);
    return () => {
      map.current?.off('click', handleClick);
    };
  }, [isPlacingCenter, isDrawingPolygon]);

  // Draw geofences on map
  const drawGeofences = useCallback(() => {
    if (!map.current) return;

    // Remove existing geofence layers
    geofences.forEach((geofence) => {
      if (map.current?.getLayer(`geofence-${geofence.id}`)) {
        map.current.removeLayer(`geofence-${geofence.id}`);
      }
      if (map.current?.getLayer(`geofence-outline-${geofence.id}`)) {
        map.current.removeLayer(`geofence-outline-${geofence.id}`);
      }
      if (map.current?.getSource(`geofence-${geofence.id}`)) {
        map.current.removeSource(`geofence-${geofence.id}`);
      }
    });

    // Draw visible geofences
    geofences.forEach((geofence) => {
      if (!visibleGeofences.has(geofence.id)) return;
      if (!map.current) return;

      if (geofence.type === 'CIRCLE' && geofence.centerLatitude && geofence.centerLongitude && geofence.radius) {
        // Create circle polygon
        const center = [geofence.centerLongitude, geofence.centerLatitude];
        const radiusKm = geofence.radius / 1000;
        const circlePoints = createCirclePolygon(center as [number, number], radiusKm);

        map.current.addSource(`geofence-${geofence.id}`, {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'Polygon',
              coordinates: [circlePoints],
            },
          },
        });

        map.current.addLayer({
          id: `geofence-${geofence.id}`,
          type: 'fill',
          source: `geofence-${geofence.id}`,
          paint: {
            'fill-color': geofence.color,
            'fill-opacity': 0.2,
          },
        });

        map.current.addLayer({
          id: `geofence-outline-${geofence.id}`,
          type: 'line',
          source: `geofence-${geofence.id}`,
          paint: {
            'line-color': geofence.color,
            'line-width': 2,
          },
        });
      } else if (geofence.type === 'POLYGON' && geofence.coordinates) {
        map.current.addSource(`geofence-${geofence.id}`, {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'Polygon',
              coordinates: [geofence.coordinates],
            },
          },
        });

        map.current.addLayer({
          id: `geofence-${geofence.id}`,
          type: 'fill',
          source: `geofence-${geofence.id}`,
          paint: {
            'fill-color': geofence.color,
            'fill-opacity': 0.2,
          },
        });

        map.current.addLayer({
          id: `geofence-outline-${geofence.id}`,
          type: 'line',
          source: `geofence-${geofence.id}`,
          paint: {
            'line-color': geofence.color,
            'line-width': 2,
          },
        });
      }
    });
  }, [geofences, visibleGeofences]);

  // Redraw geofences when visibility changes
  useEffect(() => {
    if (map.current?.isStyleLoaded()) {
      drawGeofences();
    } else {
      map.current?.on('load', drawGeofences);
    }
  }, [drawGeofences]);

  // Update cursor when drawing polygon
  useEffect(() => {
    if (!map.current) return;
    map.current.getCanvas().style.cursor = isDrawingPolygon ? 'crosshair' : '';
  }, [isDrawingPolygon]);

  // Draw polygon preview while drawing
  useEffect(() => {
    if (!map.current) return;

    const cleanupPreview = () => {
      // Clear existing polygon markers
      polygonMarkersRef.current.forEach((m) => m.remove());
      polygonMarkersRef.current = [];

      // Remove existing preview layers
      if (map.current?.getLayer('polygon-preview-fill')) {
        map.current.removeLayer('polygon-preview-fill');
      }
      if (map.current?.getLayer('polygon-preview-line')) {
        map.current.removeLayer('polygon-preview-line');
      }
      if (map.current?.getSource('polygon-preview')) {
        map.current.removeSource('polygon-preview');
      }
    };

    const drawPreview = () => {
      if (!map.current || !map.current.isStyleLoaded()) return;

      cleanupPreview();

      // Only draw if we're in polygon mode and have points
      if (formData.type !== 'POLYGON' || formData.coordinates.length === 0 || (!isCreateModalOpen && !isEditModalOpen)) {
        return;
      }

      // Add markers for each point
      formData.coordinates.forEach((coord, index) => {
        const el = document.createElement('div');
        el.style.width = '20px';
        el.style.height = '20px';
        el.style.backgroundColor = formData.color;
        el.style.border = '3px solid #ffffff';
        el.style.borderRadius = '50%';
        el.style.cursor = 'move';
        el.style.boxShadow = '0 2px 8px rgba(0,0,0,0.5)';
        el.style.zIndex = '1000';

        const marker = new Marker({ element: el, draggable: true })
          .setLngLat(coord)
          .addTo(map.current!);

        marker.on('dragend', () => {
          const lngLat = marker.getLngLat();
          setFormData((prev) => {
            const newCoords = [...prev.coordinates];
            newCoords[index] = [lngLat.lng, lngLat.lat];
            return { ...prev, coordinates: newCoords };
          });
        });

        polygonMarkersRef.current.push(marker);
      });

      // Draw polygon preview if we have at least 2 points
      if (formData.coordinates.length >= 2) {
        const coords = [...formData.coordinates];
        const isPolygon = formData.coordinates.length >= 3;

        try {
          if (isPolygon) {
            coords.push(coords[0]); // Close the polygon
            map.current.addSource('polygon-preview', {
              type: 'geojson',
              data: {
                type: 'Feature',
                properties: {},
                geometry: {
                  type: 'Polygon',
                  coordinates: [coords],
                },
              },
            });

            map.current.addLayer({
              id: 'polygon-preview-fill',
              type: 'fill',
              source: 'polygon-preview',
              paint: {
                'fill-color': formData.color,
                'fill-opacity': 0.3,
              },
            });
          } else {
            map.current.addSource('polygon-preview', {
              type: 'geojson',
              data: {
                type: 'Feature',
                properties: {},
                geometry: {
                  type: 'LineString',
                  coordinates: coords,
                },
              },
            });
          }

          map.current.addLayer({
            id: 'polygon-preview-line',
            type: 'line',
            source: 'polygon-preview',
            paint: {
              'line-color': formData.color,
              'line-width': 3,
            },
          });
        } catch (err) {
          console.error('Error adding polygon layers:', err);
        }
      }
    };

    // Draw immediately if style is loaded, otherwise wait
    if (map.current.isStyleLoaded()) {
      drawPreview();
    } else {
      map.current.once('load', drawPreview);
    }

    return cleanupPreview;
  }, [formData.coordinates, formData.type, formData.color, isCreateModalOpen, isEditModalOpen]);

  // Update center marker when form data changes (only for CIRCLE type)
  useEffect(() => {
    if (!map.current) return;

    // Only show center marker for CIRCLE type
    if ((isCreateModalOpen || isEditModalOpen) && formData.type === 'CIRCLE') {
      if (centerMarker.current) {
        centerMarker.current.setLngLat([formData.centerLongitude, formData.centerLatitude]);
      } else {
        const el = document.createElement('div');
        el.className = 'w-6 h-6 bg-primary-500 rounded-full border-2 border-white shadow-lg';
        centerMarker.current = new Marker({ element: el, draggable: true })
          .setLngLat([formData.centerLongitude, formData.centerLatitude])
          .addTo(map.current);

        centerMarker.current.on('dragend', () => {
          const lngLat = centerMarker.current?.getLngLat();
          if (lngLat) {
            setFormData((prev) => ({
              ...prev,
              centerLatitude: lngLat.lat,
              centerLongitude: lngLat.lng,
            }));
          }
        });
      }
    } else {
      centerMarker.current?.remove();
      centerMarker.current = null;
    }
  }, [formData.centerLatitude, formData.centerLongitude, formData.type, isCreateModalOpen, isEditModalOpen]);

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: GeofenceFormData) => {
      const payload: Record<string, unknown> = {
        name: data.name,
        type: data.type,
        color: data.color,
        category: data.category || undefined,
        isActive: data.isActive,
      };

      if (data.type === 'CIRCLE') {
        payload.centerLatitude = data.centerLatitude;
        payload.centerLongitude = data.centerLongitude;
        payload.radius = data.radius;
      } else if (data.type === 'POLYGON') {
        payload.coordinates = data.coordinates;
      }

      return geofencesApi.create(payload as any);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['geofences'] });
      setIsCreateModalOpen(false);
      setIsDrawingPolygon(false);
      setFormData(defaultFormData);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<GeofenceFormData> }) => {
      const payload: Record<string, unknown> = {
        name: data.name,
        color: data.color,
        category: data.category || undefined,
        isActive: data.isActive,
      };

      if (data.type === 'CIRCLE') {
        payload.centerLatitude = data.centerLatitude;
        payload.centerLongitude = data.centerLongitude;
        payload.radius = data.radius;
      } else if (data.type === 'POLYGON' && data.coordinates) {
        payload.coordinates = data.coordinates;
      }

      return geofencesApi.update(id, payload as any);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['geofences'] });
      setIsEditModalOpen(false);
      setIsDrawingPolygon(false);
      setSelectedGeofence(null);
      setFormData(defaultFormData);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => geofencesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['geofences'] });
      setIsDeleteModalOpen(false);
      setSelectedGeofence(null);
      setVisibleGeofences((prev) => {
        const next = new Set(prev);
        if (selectedGeofence) next.delete(selectedGeofence.id);
        return next;
      });
    },
  });

  // Handlers
  const handleOpenCreate = () => {
    // Use current map center instead of default Paris location
    const currentCenter = map.current?.getCenter();
    const newFormData = {
      ...defaultFormData,
      centerLatitude: currentCenter?.lat || defaultFormData.centerLatitude,
      centerLongitude: currentCenter?.lng || defaultFormData.centerLongitude,
    };
    setFormData(newFormData);
    setFormErrors({});
    setIsCreateModalOpen(true);
    // Don't move the map - keep current view
  };

  const handleOpenEdit = (geofence: Geofence) => {
    setSelectedGeofence(geofence);
    setFormData({
      name: geofence.name,
      type: geofence.type,
      color: geofence.color,
      centerLatitude: geofence.centerLatitude || 48.8566,
      centerLongitude: geofence.centerLongitude || 2.3522,
      radius: geofence.radius || 500,
      coordinates: geofence.coordinates || [],
      category: geofence.category || '',
      isActive: geofence.isActive,
    });
    setFormErrors({});
    setIsEditModalOpen(true);

    // Center map on geofence
    if (geofence.type === 'POLYGON' && geofence.coordinates && geofence.coordinates.length > 0) {
      // Calculate bounds for polygon
      const lngs = geofence.coordinates.map((c) => c[0]);
      const lats = geofence.coordinates.map((c) => c[1]);
      const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
      const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
      map.current?.flyTo({
        center: [centerLng, centerLat],
        zoom: 14,
      });
    } else if (geofence.centerLatitude && geofence.centerLongitude) {
      map.current?.flyTo({
        center: [geofence.centerLongitude, geofence.centerLatitude],
        zoom: 14,
      });
    }
  };

  const handleOpenDelete = (geofence: Geofence) => {
    setSelectedGeofence(geofence);
    setIsDeleteModalOpen(true);
  };

  const handleToggleVisibility = (geofenceId: string) => {
    setVisibleGeofences((prev) => {
      const next = new Set(prev);
      if (next.has(geofenceId)) {
        next.delete(geofenceId);
      } else {
        next.add(geofenceId);
      }
      return next;
    });
  };

  const handleFocusGeofence = (geofence: Geofence) => {
    // Make sure it's visible
    if (!visibleGeofences.has(geofence.id)) {
      handleToggleVisibility(geofence.id);
    }

    if (geofence.type === 'POLYGON' && geofence.coordinates && geofence.coordinates.length > 0) {
      // Calculate center from polygon coordinates
      const lngs = geofence.coordinates.map((c) => c[0]);
      const lats = geofence.coordinates.map((c) => c[1]);
      const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
      const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
      map.current?.flyTo({
        center: [centerLng, centerLat],
        zoom: 16,
      });
    } else if (geofence.centerLatitude && geofence.centerLongitude) {
      map.current?.flyTo({
        center: [geofence.centerLongitude, geofence.centerLatitude],
        zoom: 14,
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof GeofenceFormData, string>> = {};

    if (!formData.name || formData.name.length < 2) {
      errors.name = 'Nom requis (min 2 caracteres)';
    }

    if (formData.type === 'CIRCLE') {
      if (formData.radius < 10 || formData.radius > 100000) {
        errors.radius = 'Rayon entre 10m et 100km';
      }
    } else if (formData.type === 'POLYGON') {
      if (formData.coordinates.length < 3) {
        errors.coordinates = 'Minimum 3 points requis pour un polygone';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      createMutation.mutate(formData);
    }
  };

  const handleSubmitEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedGeofence && validateForm()) {
      updateMutation.mutate({ id: selectedGeofence.id, data: formData });
    }
  };

  const handleDelete = () => {
    if (selectedGeofence) {
      deleteMutation.mutate(selectedGeofence.id);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'CIRCLE':
        return Circle;
      case 'POLYGON':
        return Hexagon;
      case 'CORRIDOR':
        return Route;
      default:
        return Circle;
    }
  };

  return (
    <div className="h-[calc(100vh-64px)] flex relative">
      {/* Sidebar */}
      <aside
        className={clsx(
          'bg-white flex flex-col border-r border-gray-200 transition-all duration-300 z-20',
          sidebarOpen ? 'w-80' : 'w-0'
        )}
      >
        {sidebarOpen && (
          <>
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-gray-900">{t('geofences.title')}</h2>
            <div className="flex items-center gap-1">
              <button
                onClick={handleOpenCreate}
                className="p-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                title={t('geofences.add_zone')}
              >
                <Plus className="h-4 w-4" />
              </button>
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title={t('geofences.hide_panel')}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder={t('geofences.search_placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-2 p-4 border-b border-gray-200 bg-gray-50">
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            <p className="text-xs text-gray-500">{t('geofences.stat_total')}</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-600">{stats.active}</p>
            <p className="text-xs text-gray-500">{t('geofences.stat_active')}</p>
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-6 w-6 animate-spin text-primary-500" />
            </div>
          ) : filteredGeofences.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              <MapPin className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p>{t('geofences.no_zone')}</p>
            </div>
          ) : (
            filteredGeofences.map((geofence) => {
              const TypeIcon = getTypeIcon(geofence.type);
              const isVisible = visibleGeofences.has(geofence.id);

              return (
                <div
                  key={geofence.id}
                  className="p-3 border-b border-gray-200 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <button
                      onClick={() => handleFocusGeofence(geofence)}
                      className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${geofence.color}20` }}
                    >
                      <TypeIcon className="h-5 w-5" style={{ color: geofence.color }} />
                    </button>
                    <div className="flex-1 min-w-0">
                      <button
                        onClick={() => handleFocusGeofence(geofence)}
                        className="font-medium text-gray-900 text-left hover:text-primary-600 truncate block w-full"
                      >
                        {geofence.name}
                      </button>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-500">
                          {geofence.type === 'CIRCLE' && geofence.radius
                            ? `${geofence.radius}m`
                            : geofence.type}
                        </span>
                        {geofence.category && (
                          <span className="text-xs px-1.5 py-0.5 bg-gray-100 rounded text-gray-600">
                            {categoryOptions.find((c) => c.value === geofence.category)?.label ||
                              geofence.category}
                          </span>
                        )}
                        {!geofence.isActive && (
                          <span className="text-xs px-1.5 py-0.5 bg-red-100 rounded text-red-600">
                            {t('geofences.inactive')}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleToggleVisibility(geofence.id)}
                        className={clsx(
                          'p-1.5 rounded transition-colors',
                          isVisible
                            ? 'text-primary-600 bg-primary-50'
                            : 'text-gray-400 hover:text-gray-600'
                        )}
                        title={isVisible ? t('geofences.hide') : t('geofences.show')}
                      >
                        {isVisible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => handleOpenEdit(geofence)}
                        className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
                        title={t('geofences.edit')}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleOpenDelete(geofence)}
                        className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                        title={t('geofences.delete')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
          </>
        )}
      </aside>

      {/* Toggle sidebar button */}
      {!sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(true)}
          className="absolute left-4 top-4 z-20 bg-white p-2.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors shadow-md"
          title={t('geofences.show_panel')}
        >
          <ChevronRight className="h-5 w-5 text-gray-700" />
        </button>
      )}

      {/* Map */}
      <div className="flex-1 relative">
        <div ref={mapContainer} className="w-full h-full" />

        {/* Placing center indicator */}
        {isPlacingCenter && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-primary-600 text-white px-4 py-2 rounded-lg shadow-lg">
            {t('geofences.click_to_place_center')}
          </div>
        )}

        {/* Drawing polygon indicator */}
        {isDrawingPolygon && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-primary-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-3">
            <span>{t('geofences.click_to_add_points', { count: formData.coordinates.length })}</span>
            <button
              onClick={() => setIsDrawingPolygon(false)}
              className="px-2 py-1 bg-white/20 rounded hover:bg-white/30"
            >
              {t('geofences.finish')}
            </button>
          </div>
        )}
      </div>

      {/* Create/Edit Side Panel */}
      {(isCreateModalOpen || isEditModalOpen) && (
        <div className="absolute top-0 right-0 h-full w-96 bg-white shadow-2xl z-40 flex flex-col border-l border-gray-200">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-900">
              {isCreateModalOpen ? t('geofences.modal_create_title') : t('geofences.modal_edit_title')}
            </h2>
            <button
              onClick={() => {
                setIsCreateModalOpen(false);
                setIsEditModalOpen(false);
                setIsDrawingPolygon(false);
                setSelectedGeofence(null);
                setFormData(defaultFormData);
              }}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={isCreateModalOpen ? handleSubmitCreate : handleSubmitEdit} className="flex-1 flex flex-col">
            <div className="flex-1 px-4 py-4 space-y-4 overflow-y-auto">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('geofences.field_name')}
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder={t('geofences.name_placeholder')}
                      className={clsx(
                        'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                        formErrors.name ? 'border-red-500' : 'border-gray-300'
                      )}
                    />
                    {formErrors.name && (
                      <p className="mt-1 text-sm text-red-500">{formErrors.name}</p>
                    )}
                  </div>

                  {/* Type (only for create) */}
                  {isCreateModalOpen && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('geofences.field_type')}
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { value: 'CIRCLE', label: t('geofences.type_circle'), icon: Circle },
                          { value: 'POLYGON', label: t('geofences.type_polygon'), icon: Hexagon },
                        ].map((type) => (
                          <button
                            key={type.value}
                            type="button"
                            onClick={() => {
                              setFormData({ ...formData, type: type.value as 'CIRCLE' | 'POLYGON', coordinates: [] });
                              setIsDrawingPolygon(false);
                            }}
                            className={clsx(
                              'flex flex-col items-center p-3 rounded-lg border-2 transition-colors',
                              formData.type === type.value
                                ? 'border-primary-500 bg-primary-50'
                                : 'border-gray-200 hover:border-gray-300'
                            )}
                          >
                            <type.icon
                              className={clsx(
                                'h-6 w-6 mb-1',
                                formData.type === type.value ? 'text-primary-600' : 'text-gray-500'
                              )}
                            />
                            <span
                              className={clsx(
                                'text-sm',
                                formData.type === type.value ? 'text-primary-700' : 'text-gray-600'
                              )}
                            >
                              {type.label}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Category */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('geofences.field_category')}
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {categoryOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Center coordinates */}
                  {formData.type === 'CIRCLE' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('geofences.field_center')}
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <input
                              type="number"
                              step="any"
                              value={formData.centerLatitude}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  centerLatitude: parseFloat(e.target.value) || 0,
                                })
                              }
                              placeholder={t('geofences.lat_placeholder')}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                            />
                          </div>
                          <div>
                            <input
                              type="number"
                              step="any"
                              value={formData.centerLongitude}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  centerLongitude: parseFloat(e.target.value) || 0,
                                })
                              }
                              placeholder={t('geofences.lng_placeholder')}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                            />
                          </div>
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                          {t('geofences.center_hint')}
                        </p>
                      </div>

                      {/* Radius */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('geofences.field_radius')}
                        </label>
                        <input
                          type="number"
                          min={10}
                          max={100000}
                          value={formData.radius}
                          onChange={(e) =>
                            setFormData({ ...formData, radius: parseInt(e.target.value) || 500 })
                          }
                          className={clsx(
                            'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500',
                            formErrors.radius ? 'border-red-500' : 'border-gray-300'
                          )}
                        />
                        {formErrors.radius && (
                          <p className="mt-1 text-sm text-red-500">{formErrors.radius}</p>
                        )}
                      </div>
                    </>
                  )}

                  {/* Polygon drawing controls */}
                  {formData.type === 'POLYGON' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('geofences.field_polygon_path')}
                      </label>
                      <div className="space-y-3">
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setIsDrawingPolygon(!isDrawingPolygon)}
                            className={clsx(
                              'flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border-2 transition-colors',
                              isDrawingPolygon
                                ? 'border-primary-500 bg-primary-50 text-primary-700'
                                : 'border-gray-300 text-gray-700 hover:border-gray-400'
                            )}
                          >
                            <Hexagon className="h-4 w-4" />
                            {isDrawingPolygon ? 'Dessin en cours...' : 'Dessiner sur la carte'}
                          </button>
                          {formData.coordinates.length > 0 && (
                            <button
                              type="button"
                              onClick={() => setFormData({ ...formData, coordinates: [] })}
                              className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
                            >
                              Effacer
                            </button>
                          )}
                        </div>

                        {isDrawingPolygon && (
                          <p className="text-sm text-primary-600 bg-primary-50 p-2 rounded-lg">
                            Cliquez sur la carte pour ajouter des points. Minimum 3 points requis.
                          </p>
                        )}

                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-500">
                            Points: <strong className="text-gray-900">{formData.coordinates.length}</strong>
                          </span>
                          {formData.coordinates.length >= 3 && (
                            <span className="text-green-600 flex items-center gap-1">
                              <Check className="h-4 w-4" /> Polygone valide
                            </span>
                          )}
                        </div>

                        {formErrors.coordinates && (
                          <p className="text-sm text-red-500">{formErrors.coordinates}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Color */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Couleur
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {colorOptions.map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setFormData({ ...formData, color })}
                          className={clsx(
                            'w-8 h-8 rounded-full flex items-center justify-center transition-transform',
                            formData.color === color && 'ring-2 ring-offset-2 ring-gray-400'
                          )}
                          style={{ backgroundColor: color }}
                        >
                          {formData.color === color && <Check className="h-4 w-4 text-white" />}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Active */}
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <label htmlFor="isActive" className="text-sm text-gray-700">
                      Zone active (génère des alertes)
                    </label>
                  </div>
                </div>

            {/* Footer */}
            <div className="flex gap-3 px-4 py-4 border-t border-gray-200 bg-gray-50">
              <button
                type="button"
                onClick={() => {
                  setIsCreateModalOpen(false);
                  setIsEditModalOpen(false);
                  setIsDrawingPolygon(false);
                  setSelectedGeofence(null);
                  setFormData(defaultFormData);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {createMutation.isPending || updateMutation.isPending
                  ? 'Chargement...'
                  : isCreateModalOpen
                  ? 'Créer'
                  : 'Enregistrer'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Delete Modal */}
      {isDeleteModalOpen && selectedGeofence && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setIsDeleteModalOpen(false)} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-center text-gray-900 mb-2">
                Supprimer la zone
              </h3>
              <p className="text-center text-gray-500 mb-6">
                Etes-vous sur de vouloir supprimer <strong>{selectedGeofence.name}</strong> ? Cette
                action est irreversible.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteMutation.isPending ? 'Suppression...' : 'Supprimer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Create a circle polygon from center and radius
 */
function createCirclePolygon(center: [number, number], radiusKm: number, points = 64): [number, number][] {
  const coords: [number, number][] = [];
  const distanceX = radiusKm / (111.32 * Math.cos((center[1] * Math.PI) / 180));
  const distanceY = radiusKm / 110.574;

  for (let i = 0; i < points; i++) {
    const theta = (i / points) * (2 * Math.PI);
    const x = distanceX * Math.cos(theta);
    const y = distanceY * Math.sin(theta);
    coords.push([center[0] + x, center[1] + y]);
  }
  coords.push(coords[0]); // Close the polygon

  return coords;
}
